import {
	Route,
	createBrowserRouter,
	createRoutesFromElements,
} from 'react-router-dom';

import { productCenterApi } from 'api';
import RootAuthentication from 'main/auth/RootAuthentication';
import { AuthProviderStorage } from 'main/auth/provider';
import HomeAboutPage from 'pages/home/HomeAboutPage';
import HomePage from 'pages/home/HomePage';
import ReleaseNotesPage from 'pages/home/ReleaseNotesPage';
import TrainingsPage from 'pages/home/TrainingsPage';
import { NotFoundPage } from 'pages/notfound/NotFoundPage';

import { getAdministrationRoutes } from './getAdministrationRoutes';
import { getDevelopersRoutes } from './getDevelopersRoutes';
import { getDrivelineviewRoutes } from './getDrivelineviewRoutes';
import { getErrorRoutes } from './getErrorRoutes';
import { getHealthCheckRoutes } from './getHealthCheckRoutes';
import { getMaintenancePlannerRoutes } from './getMaintenancePlannerRoutes';
import { getOrderInfoRoutes } from './getOrderInfoRoutes';
import { getProductIndividualRoutes } from './getProductIndividualRoutes';
import { getRegisterRoutes } from './getRegisterRoutes';
import { getSearchRoutes } from './getSearchRoutes';
import { getServiceContractRoutes } from './getServiceContractRoutes';
import { getServiceProtocolRoutes } from './getServiceProtocolRoutes';
import { getUnitOverviewRoutes } from './getUnitOverviewRoutes';
import { getUnitRoutes } from './getUnitRoutes';
import {
	drivelineviewParam,
	healthCheckArea,
	home_About,
	home_ReleaseNotes,
	home_Trainings,
	orderInfoPageUrl,
	productIndividualviewAreaUrl,
	resolvedUnitIds,
	root,
	serviceContractArea,
	serviceProtocolArea,
	unitId,
	unitRelativeUrl,
} from './urls/RouteUrls';
import FleetOperationsPage from 'pages/fleetoperations/FleetOperationsPage';
import FlopModuleFederatedRegistryRoot from 'pages/fleetoperations/FlopModuleFederatedRegistryRoot';

export const getRouter = () =>
	createBrowserRouter(
		createRoutesFromElements([
			<Route
				id={root}
				key={root}
				element={<RootAuthentication />}
				shouldRevalidate={(x) =>
					x.currentParams[unitId] !== x.nextParams[unitId]
				}
				loader={async ({ params }) => {
					const unitIdRouteParam = params[unitId];

					if (unitIdRouteParam) {
						return {
							[resolvedUnitIds]:
								await resolveUnitId(unitIdRouteParam),
						};
					}

					return {};
				}}>
				<Route index element={<HomePage />} />
				<Route path={home_About} element={<HomeAboutPage />} />
				<Route path={home_Trainings} element={<TrainingsPage />} />
				<Route
					path={home_ReleaseNotes}
					element={<ReleaseNotesPage />}
				/>
				<Route path="search">{getSearchRoutes()}</Route>
				<Route path="register">{getRegisterRoutes()}</Route>
				<Route path="developers">{getDevelopersRoutes()}</Route>
				<Route path={`unitoverview/${unitRelativeUrl}`}>
					{getUnitOverviewRoutes()}
				</Route>
				<Route path={`drivelineview/${drivelineviewParam}`}>
					{getDrivelineviewRoutes()}
				</Route>
				<Route path={productIndividualviewAreaUrl}>
					{getProductIndividualRoutes()}
				</Route>
				<Route path="unit">{getUnitRoutes()}</Route>
				<Route path="MaintenancePlanner">
					{getMaintenancePlannerRoutes()}
				</Route>
				<Route
					path={"FleetOperations/*"}
					element={<FlopModuleFederatedRegistryRoot><FleetOperationsPage /></FlopModuleFederatedRegistryRoot>}
				/>
				<Route path="administration">{getAdministrationRoutes()}</Route>
				<Route path={orderInfoPageUrl}>{getOrderInfoRoutes()}</Route>
				<Route path={serviceProtocolArea}>
					{getServiceProtocolRoutes()}
				</Route>
				<Route path={healthCheckArea}>{getHealthCheckRoutes()}</Route>
				<Route path={serviceContractArea}>
					{getServiceContractRoutes()}
				</Route>
				<Route path="errors">{getErrorRoutes()}</Route>
				<Route path="notfound" element={<NotFoundPage />} />
				<Route path="*" element={<NotFoundPage />} />
			</Route>,
		]),
		{
			basename: import.meta.env.BASE_URL,
		}
	);

async function resolveUnitId(unitIdRouteParam: string | undefined) {
	if (!unitIdRouteParam) {
		return undefined;
	}

	await AuthProviderStorage.getInitializerPromise();
	const provider = AuthProviderStorage.getInstance().getProvider();
	while ((await provider.isAuthenticated()) === false) {
		await new Promise((resolve) => setTimeout(resolve, 100));
	}

	// api call has cache header for indefinite cache, so only 1 actual call will happen
	const unitIds = (
		await productCenterApi.resolveUnitIdsFromUrlVisibleToUser(
			unitIdRouteParam
		)
	).data;
	return unitIds;
}
